'use client'

import { useCallback, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Key,
  KeyRound,
  Loader2,
  Link as LinkIcon,
  ArrowLeft,
} from 'lucide-react'
import { CrmProviderCard } from './crm-provider-card'
import { CrmProviderLogo } from './crm-provider-logo'
import type { CrmProviderInfo, CrmIntegrationApiError } from '@/hooks/integrations/use-crm-integrations'
import type { CrmAuthMethodInfo } from '@/lib/crm/adapters/registry'
import type { CrmProviderType } from '@/lib/crm/types'
import { normalizeSubdomain } from '@/lib/crm/normalize-subdomain'

interface AddCrmIntegrationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  providers: CrmProviderInfo[]
  onConnect: (
    provider: CrmProviderType,
    name: string,
    credentials: Record<string, unknown>,
    metadata?: Record<string, unknown>,
  ) => Promise<{ success: boolean; error?: string }>
  /**
   * Optional OAuth install handler. When provided, providers that expose an `oauth`
   * connect method offer it in the picker; the dialog calls this to pre-create the pending
   * integration and obtain the provider's authorize URL, then redirects the browser to it.
   * Surfaces that don't wire this simply won't show the OAuth method (graceful fallback).
   */
  onPrepareOAuth?: (
    provider: CrmProviderType,
    subdomain: string,
    name: string,
  ) => Promise<{ success: boolean; integrationId?: string; authorizeUrl?: string; error?: string }>
  isConnecting: boolean
  error: CrmIntegrationApiError | null
  onClearError: () => void
}

type DialogStep = 'select-provider' | 'select-method' | 'enter-credentials'

export function AddCrmIntegrationDialog({
  open,
  onOpenChange,
  providers,
  onConnect,
  onPrepareOAuth,
  isConnecting,
  error,
  onClearError,
}: AddCrmIntegrationDialogProps) {
  const [step, setStep] = useState<DialogStep>('select-provider')
  const [selectedProvider, setSelectedProvider] = useState<CrmProviderInfo | null>(null)
  const [selectedMethod, setSelectedMethod] = useState<CrmAuthMethodInfo | null>(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({})
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [redirecting, setRedirecting] = useState(false)
  // Tracks whether the user has manually edited the Integration Name. Until they do, we keep it
  // auto-filled from the subdomain so the field is never left blank for the common one-account case.
  const [nameTouched, setNameTouched] = useState(false)

  // Methods we can actually action on this surface. OAuth needs a prepare handler; without one
  // (e.g. a surface that didn't wire onPrepareOAuth) we hide it so the picker never offers a
  // dead-end option. long_token is always actionable via onConnect.
  const methodsFor = useCallback(
    (provider: CrmProviderInfo): CrmAuthMethodInfo[] =>
      provider.authMethods.filter((m) => m.method !== 'oauth' || Boolean(onPrepareOAuth)),
    [onPrepareOAuth],
  )

  const availableMethods = useMemo(
    () => (selectedProvider ? methodsFor(selectedProvider) : []),
    [selectedProvider, methodsFor],
  )

  const resetDialog = useCallback(() => {
    setStep('select-provider')
    setSelectedProvider(null)
    setSelectedMethod(null)
    setShowInstructions(false)
    setShowPassword({})
    setFormData({})
    setRedirecting(false)
    setNameTouched(false)
    onClearError()
  }, [onClearError])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        resetDialog()
      }
      onOpenChange(newOpen)
    },
    [onOpenChange, resetDialog],
  )

  const goToCredentials = useCallback((method: CrmAuthMethodInfo) => {
    setSelectedMethod(method)
    setShowInstructions(false)
    const initialData: Record<string, string> = {}
    method.fields.forEach((field) => {
      initialData[field.name] = ''
    })
    setFormData(initialData)
    setNameTouched(false)
    setStep('enter-credentials')
  }, [])

  const handleProviderSelect = useCallback(
    (provider: CrmProviderInfo) => {
      setSelectedProvider(provider)
      onClearError()
      const methods = methodsFor(provider)
      // Single method → skip the picker and go straight to its credentials step.
      if (methods.length <= 1) {
        goToCredentials(methods[0] ?? provider.authMethods[0])
      } else {
        setStep('select-method')
      }
    },
    [methodsFor, goToCredentials, onClearError],
  )

  const handleBack = useCallback(() => {
    onClearError()
    if (step === 'enter-credentials' && availableMethods.length > 1) {
      // Back to the method picker, keeping the chosen provider.
      setSelectedMethod(null)
      setFormData({})
      setStep('select-method')
      return
    }
    // Back to the provider grid.
    setSelectedProvider(null)
    setSelectedMethod(null)
    setFormData({})
    setStep('select-provider')
  }, [step, availableMethods.length, onClearError])

  const handleLongTokenSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!selectedProvider || !selectedMethod) return

      const name = formData.name || `My ${selectedProvider.name} Integration`
      const credentials: Record<string, unknown> = {}
      const metadata: Record<string, unknown> = {}

      selectedMethod.fields.forEach((field) => {
        if (field.name === 'name') return
        if (field.name === 'subdomain') {
          // Subdomain is both a credential and identifying metadata. Normalize defensively in case
          // a value slipped in without going through the field's onChange.
          const subdomain = normalizeSubdomain(formData[field.name] || '')
          credentials[field.name] = subdomain
          metadata[field.name] = subdomain
        } else {
          credentials[field.name] = formData[field.name]
        }
      })

      const result = await onConnect(selectedProvider.id, name, credentials, metadata)
      if (result.success) {
        handleOpenChange(false)
      }
    },
    [selectedProvider, selectedMethod, formData, onConnect, handleOpenChange],
  )

  const handleOAuthInstall = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!selectedProvider || !selectedMethod || !onPrepareOAuth) return

      const subdomain = normalizeSubdomain(formData.subdomain || '')
      const name = formData.name || `My ${selectedProvider.name} Integration`

      setRedirecting(true)
      const result = await onPrepareOAuth(selectedProvider.id, subdomain, name)
      if (!result.success || !result.authorizeUrl) {
        setRedirecting(false)
        return
      }

      // Full-page redirect into the provider's authorize URL (returned by the authenticated
      // prepare/start fetches). The OAuth callback lands the user back on the current path with
      // ?<provider>_oauth=success|error, where a global handler surfaces a toast and integration
      // lists refetch on the reload.
      window.location.assign(result.authorizeUrl)
    },
    [selectedProvider, selectedMethod, onPrepareOAuth, formData],
  )

  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPassword((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }))
  }, [])

  const handleFieldChange = useCallback(
    (fieldName: string, rawValue: string) => {
      if (fieldName === 'name') setNameTouched(true)
      setFormData((prev) => {
        if (fieldName === 'subdomain') {
          const subdomain = normalizeSubdomain(rawValue)
          const next: Record<string, string> = { ...prev, subdomain }
          // Mirror the subdomain into the friendly name until the user edits the name themselves.
          if (!nameTouched && 'name' in prev) {
            next.name = subdomain
          }
          return next
        }
        return { ...prev, [fieldName]: rawValue }
      })
    },
    [nameTouched],
  )

  const isOAuth = selectedMethod?.method === 'oauth'

  const isFormValid = selectedMethod?.fields.every(
    (field) => !field.required || (formData[field.name] && formData[field.name].trim().length > 0),
  )

  const busy = isOAuth ? redirecting : isConnecting

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          {step === 'select-provider' ? (
            <>
              <DialogTitle>Connect CRM</DialogTitle>
              <DialogDescription>
                Choose a CRM provider to connect with your account
              </DialogDescription>
            </>
          ) : selectedProvider ? (
            <>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleBack} className="h-8 w-8 p-0">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <DialogTitle className="flex items-center gap-2">
                  <CrmProviderLogo provider={selectedProvider.id} logoUrl={selectedProvider.logoUrl} size={20} />
                  <span>Connect</span> {selectedProvider.name}
                </DialogTitle>
              </div>
              <DialogDescription>
                {step === 'select-method' ? 'Choose how to connect' : selectedProvider.description}
              </DialogDescription>
            </>
          ) : null}
        </DialogHeader>

        {step === 'select-provider' ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
            {providers.map((provider) => (
              <CrmProviderCard
                key={provider.id}
                provider={provider}
                onClick={() => handleProviderSelect(provider)}
              />
            ))}
          </div>
        ) : step === 'select-method' && selectedProvider ? (
          <div className="grid grid-cols-2 gap-3 py-2">
            {availableMethods.map((method) => (
              <button
                key={method.method}
                type="button"
                onClick={() => goToCredentials(method)}
                className="flex flex-col items-start gap-2 rounded-lg border border-gray-200 p-4 text-left transition-colors hover:border-blue-400 hover:bg-blue-50"
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-medium text-gray-900">
                    {method.method === 'oauth' ? (
                      <LinkIcon className="h-4 w-4 text-blue-600" />
                    ) : (
                      <KeyRound className="h-4 w-4 text-gray-500" />
                    )}
                    {method.title}
                  </span>
                  {method.recommended && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">{method.description}</p>
              </button>
            ))}
          </div>
        ) : selectedProvider && selectedMethod ? (
          <form
            onSubmit={isOAuth ? handleOAuthInstall : handleLongTokenSubmit}
            className="space-y-4"
            autoComplete="off"
          >
            {error && (
              <Alert variant="destructive">
                <AlertDescription>
                  {error.error}
                  {error.troubleshooting && error.troubleshooting.length > 0 && (
                    <ul className="list-disc list-inside mt-2 text-xs">
                      {error.troubleshooting.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Instructions (per method, e.g. how to generate a long-lived token) */}
            {selectedMethod.instructions && selectedMethod.instructions.length > 0 && (
              <div className="border border-blue-200 rounded-lg bg-blue-50">
                <button
                  type="button"
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="w-full flex items-center justify-between p-3 text-blue-900 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-sm">{selectedMethod.instructionsTitle}</span>
                  </div>
                  {showInstructions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showInstructions && (
                  <div className="p-3 border-t border-blue-200 bg-blue-50">
                    <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                      {selectedMethod.instructions.map((instruction) => (
                        <li key={instruction.step}>{instruction.text}</li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-4">
              {selectedMethod.fields.map((field) => (
                <div key={field.name}>
                  <Label htmlFor={`crm-${field.name}`}>
                    {field.label} <span>{field.required && '*'}</span>
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id={`crm-${field.name}`}
                      name={`crm-nofill-${field.name}`}
                      type={field.type === 'password' && !showPassword[field.name] ? 'password' : 'text'}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      required={field.required}
                      autoComplete="new-password"
                    />
                    {field.type === 'password' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => togglePasswordVisibility(field.name)}
                      >
                        {showPassword[field.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                  {field.helpText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                  )}
                  {field.name === 'subdomain' && selectedProvider?.id === 'kommo' && formData.subdomain && (
                    <p className="text-xs text-green-700 mt-1">
                      <span>We&apos;ll connect to</span>{' '}
                      <span className="font-mono">https://{formData.subdomain}.kommo.com</span>
                    </p>
                  )}
                </div>
              ))}
            </div>

            {isOAuth ? (
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-xs text-blue-800">
                  <span>You&apos;ll be redirected to</span> {selectedProvider.name} <span>to authorize access. Tokens
                  are stored encrypted and refresh automatically.</span>
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-xs text-green-800">
                  <strong>Secure:</strong> <span>Your credentials will be encrypted and stored securely.</span>
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="submit"
                disabled={busy || !isFormValid}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isOAuth ? 'Redirecting...' : 'Connecting...'}
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-4 h-4 mr-2" />
                    {selectedMethod.actionLabel}
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
